import { IInsight, InsightType } from '@jumpstart/core';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import LinkIcon from '@mui/icons-material/Link';
import { Formik } from 'formik';
import React, { useRef } from 'react';
import * as yup from 'yup';
import ScoutTooltip from '../../../widgets/ScoutTooltip';
import ContentPreviewEvent from './ContentPreviewEvent';
import ContentPreviewLoading from './ContentPreviewLoading';
import { cLength, CLIMITS } from './DialogFormConfig';
import EventFormEdit from './EventFormEdit';
import { IValidation } from './utils';

const defaultEvent = (): IInsight => {
  return {
    id: 'unknown',
    updatedAt: new Date().toISOString(),
    createdAt: new Date().toISOString(),
    name: '',
    description: '',
    tags: [],
    highlights: [],
    widgets: [],
    widgetsMulti: {
      title: '',
      text: '',
      action: ''
    },
    type: InsightType.DEFAULT,
    content: {
      indicators: []
    }
  };
};

const eventFormSchema = yup.object({
  active: yup.boolean().optional(),
  name: yup
    .string()
    .required('Name is required'),
  description: yup
    .string()
    .nullable(),
  type: yup
    .string().nullable(),
  severity: yup
    .string()
    .required('Severity is required'),
  tags: yup.array().of(yup.string()).nullable(),
  timelineStart: yup.number().nullable().optional(),
  timelineEnd: yup.number().nullable().optional(),
  timelineBreedOffset: yup.number().nullable().optional(),
  recurringValue: yup.number().nullable().optional(),
  recurringUnit: yup.string().nullable(),
  widgetsMulti: yup.object({
    title: yup.string(),
    text: yup.string(),
    action: yup.string()
  }).nullable(),
  widgets: yup.array().of(yup.object({
    widget: yup.string(),
    purpose: yup.string(),
    title: yup.string(),
    meta: yup.object({
      type: yup.string(),
      subtype: yup.string(),
      severity: yup.string()
    }),
    requirements: yup.array().of(yup.string())
  })).nullable(),
  highlights: yup.array()
    .of(yup.string())
    .nullable()
    .test('len', `Highlights/Instructions must be less than ${CLIMITS.highlights} characters each`, val => {
      if (val) {
        for (const highlight of val) {
          if (!cLength(CLIMITS.highlights, highlight)) {
            return false;
          }
        }
      }
      return true;
    }),
  tips: yup.array()
    .of(yup.object({
    message: yup.string(),
    severity: yup.string()
  }))
    .nullable()
    .test('len', `Tips must be less than ${CLIMITS.tips} characters each`, val => {
    if (val) {
      for (const tip of val) {
        if (!cLength(CLIMITS.tips, tip.message)) {
          return false;
        }
      }
    }
    return true;
  })
});

export interface IEventEditor {
  event: IInsight;
  loading: boolean;
  validation: IValidation;

  onError?(err: Error): void;

  onSubmit?(event: IInsight): void;
}

export default function EventEditor(props: IEventEditor) {
  const { event, onError, onSubmit, loading, validation } = props;
  const frameContainer = useRef<any>(null);

  return (
    <Grid container direction="column" style={{ padding: 24 }}>
      <Formik
        initialValues={{
          ...defaultEvent(),
          ...event
        }}
        validationSchema={eventFormSchema}
        onSubmit={(values) => {
          if (onSubmit) {
            console.log('FORM', values);
            onSubmit({
              ...values,
              recurringValue: typeof values.recurringValue === 'string' ? parseInt(
                values.recurringValue as any,
                10
              ) : values.recurringValue,
              timelineStart: typeof values.timelineStart === 'string' ? parseInt(
                values.timelineStart as any,
                10
              ) : values.timelineStart,
              timelineEnd: typeof values.timelineEnd === 'string' ? parseInt(
                values.timelineEnd as any,
                10
              ) : values.timelineEnd,
              timelineBreedOffset: typeof values.timelineBreedOffset === 'string' ? parseInt(
                values.timelineBreedOffset as any,
                10
              ) : values.timelineBreedOffset
            });
          }
        }}
      >
        {
          (formikProps) => (

            <Grid container direction="row" justifyContent="space-between" wrap="nowrap">
              <Grid item xs={5}>
                <Paper style={{ height: '100%', padding: 24 }}>
                  <Grid item>
                    <Grid container direction="row" alignItems="center" style={{ marginBottom: 8 }}>
                      <Typography variant="h6" style={{ marginRight: 8 }}>Event Form</Typography>
                      {event && <Link
                        href={`https://app.graphcms.com/d8a43d9d6e4547da83051c63f594b7f0/master/content/648979d9c06d4e2389aa9944ab4d9efe/view/53c18833e12b49a2980754996c104ad5/${event.id}`}
                        target={`graphcms_${event.id}`}
                      >
                        <LinkIcon/>
                      </Link>}
                    </Grid>
                    {event &&
                    <Typography variant="caption" style={{ marginBottom: 32 }}>You can also edit the form in the <Link
                      href={`https://app.graphcms.com/d8a43d9d6e4547da83051c63f594b7f0/master/content/648979d9c06d4e2389aa9944ab4d9efe/view/53c18833e12b49a2980754996c104ad5/${event.id}`}
                      target={`graphcms_${event.id}`}
                    >GraphCMS console</Link> (including image and enriched fields)</Typography>}
                  </Grid>

                  <EventFormEdit {...formikProps} validation={validation}/>

                </Paper>
              </Grid>
              <Grid item xs={2}>
                <Grid
                  container
                  direction="column"
                  alignItems="center"
                  justifyContent="space-around"
                  style={{ height: '100%' }}
                >
                  {[1, 2, 3, 4].map((i) =>
                    <Grid item key={`reload-${i}`}>
                      <ScoutTooltip title="Save and reload changes">
                        <IconButton
                          size="medium"
                          onClick={() => formikProps.handleSubmit()}
                        >
                          <DoubleArrowIcon fontSize="large"/>
                        </IconButton>
                      </ScoutTooltip>
                    </Grid>
                  )}
                </Grid>
              </Grid>
              <Grid item xs={5} ref={frameContainer}>
                <Paper style={{ height: '100%' }}>
                  {frameContainer.current && (loading ? <ContentPreviewLoading
                    width={frameContainer.current.offsetWidth}
                    height={frameContainer.current.offsetHeight}
                  /> : <ContentPreviewEvent
                    event={event}
                    title={`${event.name}`}
                    width={frameContainer.current.offsetWidth}
                    height={frameContainer.current.offsetHeight}
                  />)}
                </Paper>
              </Grid>
            </Grid>
          )}
      </Formik>
    </Grid>
  );
}
